import { useState, useMemo, useCallback } from "react";
import { Typography, Box, Alert } from "@mui/material";
import { useDeals, useAuthentication } from "hooks";
import { BaseGrid, DealGridSkeleton, WeekButtonGroup, DealCard } from "components";
import { getCurrentDay } from "utilities";

const defaultFilters = {};

export function DealGrid({
  sortBy = null,
  searchQuery = "",
  filters = defaultFilters,
  hideWeekButtons = false,
}) {
  const { filterDay } = filters;
  const { user } = useAuthentication();
  const [selectedDay, setSelectedDay] = useState(filterDay || getCurrentDay());

  const { deals, loading, error, likeDeal, unlikeDeal } = useDeals(
    sortBy,
    selectedDay,
    searchQuery,
    filters,
  );

  const handleLike = useCallback(
    (id) => likeDeal(id),
    [likeDeal],
  );

  const handleUnlike = useCallback(
    (id) => unlikeDeal(id),
    [unlikeDeal],
  );

  const renderedDeals = useMemo(() => {
    if (deals.length === 0) return null;

    return deals.map((deal) => {
      const isLiked = !!(user && deal.likedBy?.includes(user.uid));
      const isOwner = user?.uid === deal.userId;

      return (
        <DealCard
          key={deal.id}
          deal={deal}
          onLike={handleLike}
          onUnlike={handleUnlike}
          isLiked={isLiked}
          currentUser={user}
          isOwner={isOwner}
          showTimeTypography={!hideWeekButtons}
        />
      );
    });
  }, [deals, user, handleLike, handleUnlike, hideWeekButtons]);

  if (loading) {
    return <DealGridSkeleton showWeekButtons={!hideWeekButtons} />;
  }

  if (error) {
    return (
      <Alert severity="error">
        <Typography variant="body1">Failed to load deals</Typography>
        <Typography variant="body2">{error}</Typography>
      </Alert>
    );
  }

  return (
    <Box>
      {!hideWeekButtons && (
        <WeekButtonGroup selectedDay={selectedDay} onDayChange={setSelectedDay} />
      )}

      {deals.length === 0 ? (
        <Alert severity="info">
          {searchQuery
            ? `No deals found matching "${searchQuery}"${
                selectedDay !== getCurrentDay() ? ` for ${selectedDay}` : ""
              }.`
            : selectedDay === "all"
            ? "No deals available."
            : `No deals available for ${selectedDay}.`}
        </Alert>
      ) : (
        <BaseGrid>{renderedDeals}</BaseGrid>
      )}
    </Box>
  );
}